// @flow
import React, { Fragment, cloneElement, type Element } from 'react';
import styled from 'styled-components';
import Typography from 'seedling/components/typography';
import Modal from 'seedling/components/modals/modal';
import { type ModalProps } from 'seedling/components/modals/shared';
import useModal from 'seedling/components/modals/use_modal';
import useOneTimeDisplay from 'seedling/hooks/use_one_time_display';

type Props = ModalProps & {
  announcement: string | Element<any> | function,
  announcementKey: string,
  announcementHeader: string,
  contentWidth?: string,
  screenshotAlt?: string,
  screenshotPath: string,
  screenshotTopPosition?: number,
  screenshotWidth?: string,
};

function AnnouncementModal(props: Props) {
  const {
    announcement,
    announcementKey,
    announcementHeader,
    children,
    contentWidth,
    screenshotAlt,
    screenshotPath,
    screenshotTopPosition,
    screenshotWidth,
    title,
  } = props;

  const [previouslyDisplayed, setPreviouslyDisplayed] = useOneTimeDisplay(
    'feature_announcements',
    announcementKey
  );

  const [open, openModal, closeModal] = useModal(!previouslyDisplayed);

  const close = () => {
    closeModal();
    setPreviouslyDisplayed();
  };

  const Announcement = ({ announcement }) => {
    switch (typeof announcement) {
      case 'string':
        return <Typography type="p">{announcement}</Typography>;
      case 'function':
        return announcement({ close });
      default:
        return announcement;
    }
  };

  return (
    <Fragment>
      {children != null &&
        cloneElement(children, {
          onClick: openModal,
        })}

      <HiddenOverflowModal
        {...props}
        open={open}
        title={title}
        closeModal={close}
        className={announcementKey}
        fullWidth
      >
        <div style={{ position: 'relative' }}>
          <Left contentWidth={contentWidth}>
            <Typography type="h1" marginTop={0} marginBottom={20}>
              {announcementHeader}
            </Typography>
            <Announcement announcement={announcement} />
          </Left>
          <Right contentWidth={contentWidth}>
            <Screenshot
              className="announcement-screenshot"
              alt={screenshotAlt}
              src={screenshotPath}
              top={screenshotTopPosition}
              width={screenshotWidth}
            />
          </Right>
        </div>
      </HiddenOverflowModal>
    </Fragment>
  );
}

AnnouncementModal.defaultProps = {
  displayXIcon: true,
  draggable: false,
  contentWidth: '50%',
  maxWidth: 'lg',
  screenshotTopPosition: 102,
  title: 'Announcement',
};

export default AnnouncementModal;

const Left = styled.div`
  position: relative;
  display: inline-block;
  width: ${props => props.contentWidth};
  box-sizing: border-box;
  min-height: 320px;
  line-height: 1.71;
  padding: 20px;
  vertical-align: top;
`;

const Right = styled.div`
  display: inline-block;
  width: calc(100% - ${props => props.contentWidth});
  vertical-align: top;
`;

const Screenshot = styled.img`
  height: auto;
  width: ${props => props.width};
  display: block;
  max-width: 100%;
  margin-left: auto;
`;

const HiddenOverflowModal = styled(Modal)`
  h1 {
    margin-top: 0;
    font-weight: 600;
  }

  .MuiDialog-paper {
    overflow: hidden;
  }

  .MuiDialogContent-root {
    padding: 0;
  }
`;
